import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Check, X, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

// SEO Configuration for AI Search Engines
const SEO_CONFIG = {
  title: 'Pricing Plans | Radio Analytics Subscriptions - Radio Solutions',
  description: 'Choose the right radio analytics plan for your station. From free tools to enterprise solutions with advanced playlist analysis, audience metrics, and competitive intelligence.',
  keywords: 'radio analytics pricing, radio software subscription, playlist analyzer cost, τιμές radio solutions, συνδρομή αναλυτικά ραδιοφώνου, radio station software pricing, broadcast analytics plans, radio metrics subscription, audience measurement pricing',
  ogUrl: 'https://radio-apps.online/pricing'
};

const PricingPage = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, currentLang } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [features, setFeatures] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [userSubscription, setUserSubscription] = useState(null);
  
  const backendUrl = getBackendUrl(); // Dynamic runtime URL

  // Helper to get translated name based on current language
  const getTranslatedName = (item) => {
    const langKey = `name_${currentLang === 'el' ? 'gr' : currentLang}`;
    return item[langKey] || item.name_en || item.name || item.name_gr;
  };

  // Helper to get translated description based on current language
  const getTranslatedDescription = (item) => {
    const langKey = `description_${currentLang === 'el' ? '' : currentLang}`;
    if (currentLang === 'el') return item.description;
    return item[langKey] || item.description_en || item.description;
  };

  // Fallback feature names in Greek (in case API doesn't load)
  const fallbackFeatureNames = {
    'categories_on_air': 'Κατηγορίες Θεμάτων On Air',
    'listener_calculator': 'Μετρητής Ακροατών',
    'campaign_analyzer': 'Αναλυτής Καμπάνιας',
    'radio_top_songs': 'Radio Top Songs',
    'songs_analyzer': 'Songs Analyzer',
    'peoples_chart': "The People's Chart",
    'compare_stations': 'Compare Stations',
    'monthly_support': 'Μηνιαία Υποστήριξη με Email',
    'priority_support': 'Υποστήριξη Προτεραιότητας',
    'consulting': 'Consulting'
  };

  // Helper to get translated feature name
  const getFeatureName = (featureId) => {
    const feature = features[featureId];
    
    // If feature not loaded from API, use fallback
    let name;
    if (!feature) {
      name = fallbackFeatureNames[featureId] || featureId;
    } else {
      // Return based on current language
      if (currentLang === 'el') {
        name = feature.name;
      } else {
        const langKey = `name_${currentLang}`;
        name = feature[langKey] || feature.name_en || feature.name;
      }
    }
    
    // Add PRO badge for premium features
    const proFeatures = ['songs_analyzer', 'peoples_chart', 'radio_top_songs', 'compare_stations'];
    if (proFeatures.includes(featureId)) {
      return (
        <span className="flex items-center gap-2">
          {name}
          <span className="text-[10px] bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-0.5 rounded-full font-bold">
            PRO
          </span>
        </span>
      );
    }
    
    return name;
  };

  // Check for payment status from URL params
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error(t('paymentCancelled'));
    }
  }, [searchParams]);

  // Fetch packages
  useEffect(() => {
    fetchPackages();
    if (user?.user_id || user?.id) {
      fetchUserSubscription();
    }
  }, [user]);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/packages`);
      const data = await response.json();
      setPackages(data.packages);
      setFeatures(data.features);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error(t('errorLoadingPackages'));
      setLoading(false);
    }
  };

  const fetchUserSubscription = async () => {
    try {
      const userId = user?.user_id || user?.id;
      const response = await fetch(`${backendUrl}/api/user-subscription/${userId}`);
      const data = await response.json();
      setUserSubscription(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info(t('checkEmailConfirmation'));
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        toast.success('🎉 ' + t('paymentSuccess'));
        fetchUserSubscription();
        // Clear URL params
        window.history.replaceState({}, '', '/pricing');
        return;
      } else if (data.status === 'expired') {
        toast.error(t('sessionExpired'));
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const handleSubscribe = async (packageId) => {
    if (!user) {
      // Redirect to full signup form for subscribers (need station details)
      toast.info(t('createAccountFirst') || 'Δημιουργήστε λογαριασμό για να αγοράσετε πακέτο');
      // Store the selected package to redirect after signup
      sessionStorage.setItem('pendingPackage', packageId);
      sessionStorage.setItem('pendingBillingCycle', billingCycle);
      navigate('/signup');
      return;
    }

    setProcessingPayment(true);
    
    try {
      // Check if user already has an active subscription (package change)
      if (userSubscription?.is_active && userSubscription?.package_id !== packageId) {
        // Call change-package endpoint for proration
        const changeResponse = await fetch(`${backendUrl}/api/change-package`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.user_id || user.id,
            new_package_id: packageId,
            billing_cycle: billingCycle
          })
        });
        
        const changeData = await changeResponse.json();
        
        if (changeData.action === 'payment_required') {
          // Upgrade - redirect to Stripe checkout
          toast.info(t('chargeForUpgrade').replace('{amount}', changeData.proration_amount));
          window.location.href = changeData.checkout_url;
        } else if (changeData.action === 'completed') {
          // Downgrade - applied immediately
          toast.success(changeData.message);
          fetchUserSubscription();
          setProcessingPayment(false);
        } else if (changeData.detail) {
          throw new Error(changeData.detail);
        }
        return;
      }
      
      // New subscription
      const response = await fetch(`${backendUrl}/api/checkout/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          package_id: packageId,
          billing_cycle: billingCycle,
          origin_url: window.location.origin,
          user_id: user.user_id || user.id,
          email: user.email
        })
      });

      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || t('errorCreatingPayment'));
      setProcessingPayment(false);
    }
  };

  // All features for comparison table
  const allFeatureIds = [
    'categories_on_air',
    'listener_calculator', 
    'campaign_analyzer',
    'radio_top_songs',
    'songs_analyzer',
    'peoples_chart',
    'compare_stations',
    'monthly_support',
    'priority_support',
    'consulting'  // Active only in "all" tier
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#2D0C4B] via-[#4A1D7A] to-[#6B3FA0] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9F0A]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D0C4B] via-[#4A1D7A] to-[#6B3FA0] py-8 px-4">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{SEO_CONFIG.title}</title>
        <meta name="description" content={SEO_CONFIG.description} />
        <meta name="keywords" content={SEO_CONFIG.keywords} />
        <meta property="og:title" content={SEO_CONFIG.title} />
        <meta property="og:description" content={SEO_CONFIG.description} />
        <meta property="og:url" content={SEO_CONFIG.ogUrl} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={SEO_CONFIG.ogUrl} />
      </Helmet>
      
      {/* Back Button */}
      <div className="max-w-7xl mx-auto mb-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-white/80 hover:text-[#FF9F0A] transition-colors"
        >
          <ArrowLeft size={20} />
          <span>{t('back')}</span>
        </button>
      </div>

      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {t('chooseYourPackage')}
        </h1>
        <p className="text-white/70 text-lg max-w-2xl mx-auto">
          {t('pricingDescription')}
        </p>
        
        {/* Current Subscription Badge */}
        {userSubscription?.is_active && (
          <div className="mt-4 inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full">
            <Check size={18} />
            <span>{t('currentPlan')}: {userSubscription.package_name}</span>
          </div>
        )}
      </div>

      {/* Billing Toggle */}
      <div className="flex justify-center mb-10">
        <div className="bg-white/10 p-1 rounded-full inline-flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-full font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-[#FF9F0A] text-[#2D0C4B]'
                : 'text-white/70 hover:text-white'
            }`}
          >
            {t('monthly')}
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2 rounded-full font-medium transition-all ${
              billingCycle === 'annual'
                ? 'bg-[#FF9F0A] text-[#2D0C4B]'
                : 'text-white/70 hover:text-white'
            }`}
          >
            {t('annual')}
            <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
              -17%
            </span>
          </button>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="max-w-7xl mx-auto overflow-x-auto">
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 min-w-[900px]">
          <table className="w-full">
            <thead>
              <tr>
                <th className="text-left p-4 text-white/70 font-medium">{t('features')}</th>
                {packages.map((pkg) => (
                  <th key={pkg.id} className="p-4 text-center">
                    <div 
                      className="font-bold text-lg"
                      style={{ color: pkg.color }}
                    >
                      {getTranslatedName(pkg)}
                    </div>
                    {pkg.popular && (
                      <div className="flex justify-center mt-1">
                        <span className="bg-[#FF9F0A] text-[#2D0C4B] text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Sparkles size={12} />
                          {t('popular')}
                        </span>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
              <tr className="border-b border-white/10">
                <th className="text-left p-4 text-white/70 font-medium">{t('price')}</th>
                {packages.map((pkg) => {
                  // v2.8.3 (2026-05-19): Playlister tier is now a custom-quote
                  // tier like the All-in-One popular tier — both hide the
                  // price and route to /contact instead of triggering Stripe.
                  const isContactTier = pkg.popular || pkg.id === 'playlister';
                  return (
                    <th key={pkg.id} className="p-4 text-center">
                      {isContactTier ? (
                        <div className="text-lg font-semibold text-[#FF9F0A]">
                          {currentLang === 'el' ? 'Κατόπιν Επικοινωνίας' : 'Contact Us'}
                        </div>
                      ) : (
                        <>
                          <div className="text-2xl font-bold text-white">
                            €{billingCycle === 'monthly' ? pkg.monthly_price : pkg.annual_monthly_price}
                            <span className="text-sm font-normal text-white/50">{t('perMonth')}</span>
                          </div>
                          {billingCycle === 'annual' && (
                            <div className="text-xs text-white/50 mt-1">
                              €{pkg.annual_price}{t('perYear')}
                            </div>
                          )}
                        </>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {allFeatureIds.map((featureId) => (
                <tr key={featureId} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4 text-white/80">
                    {getFeatureName(featureId)}
                  </td>
                  {packages.map((pkg) => (
                    <td key={`${pkg.id}-${featureId}`} className="p-4 text-center">
                      {pkg.features.includes(featureId) ? (
                        <div className="flex justify-center">
                          <div className="bg-green-500/20 p-1.5 rounded-full">
                            <Check size={18} className="text-green-400" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex justify-center">
                          <div className="bg-white/5 p-1.5 rounded-full">
                            <X size={18} className="text-white/30" />
                          </div>
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="p-4"></td>
                {packages.map((pkg) => {
                  // v2.8.3 (2026-05-19): Playlister now routes to /contact
                  // like the popular tier. Button label differs: Playlister
                  // shows "Επικοινωνία"/"Contact"; popular tier keeps
                  // "Αναβαθμίσου Τώρα"/"Upgrade Now".
                  const isContactTier = pkg.popular || pkg.id === 'playlister';
                  return (
                    <td key={pkg.id} className="p-4 text-center">
                      {userSubscription?.is_active && userSubscription?.package_id === pkg.id ? (
                        <button
                          disabled
                          className="w-full py-3 px-4 rounded-lg font-semibold bg-green-500/20 text-green-400 cursor-not-allowed"
                        >
                          ✓ {t('currentPackage')}
                        </button>
                      ) : isContactTier ? (
                        <button
                          onClick={() => window.location.href = 'https://radio-apps.online/contact'}
                          className="w-full py-3 px-4 rounded-lg font-semibold transition-all hover:scale-105"
                          style={{
                            background: pkg.popular
                              ? 'linear-gradient(90deg, #FF9F0A, #FFB84D)'
                              : `${pkg.color}20`,
                            color: pkg.popular ? '#2D0C4B' : pkg.color,
                            border: pkg.popular ? 'none' : `2px solid ${pkg.color}`,
                          }}
                          data-testid={pkg.id === 'playlister' ? 'playlister-contact-btn' : 'upgrade-now-btn'}
                        >
                          {pkg.popular
                            ? (currentLang === 'el' ? 'Αναβαθμίσου Τώρα' : 'Upgrade Now')
                            : (currentLang === 'el' ? 'Επικοινωνία' : 'Contact')}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSubscribe(pkg.id)}
                          disabled={processingPayment}
                          className="w-full py-3 px-4 rounded-lg font-semibold transition-all"
                          style={{
                            background: `${pkg.color}20`,
                            color: pkg.color,
                            border: `2px solid ${pkg.color}`
                          }}
                        >
                          {processingPayment ? t('pleaseWait') : t('select')}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-5xl mx-auto mt-12">
        <h3 className="text-xl font-bold text-white mb-6 text-center">
          {t('faq')}
        </h3>
        
        {/* General FAQs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
            <h4 className="font-semibold text-white mb-2">{t('canChangePackage')}</h4>
            <p className="text-white/70 text-sm">
              {t('canChangePackageAnswer')}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
            <h4 className="font-semibold text-white mb-2">{t('paymentMethods')}</h4>
            <p className="text-white/70 text-sm">
              {t('paymentMethodsAnswer')}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
            <h4 className="font-semibold text-white mb-2">{t('hasTrial')}</h4>
            <p className="text-white/70 text-sm">
              {t('hasTrialAnswer')}
            </p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="text-[#FF9F0A]">➕</span>
              {currentLang === 'el' ? 'Μπορώ να προσθέσω περισσότερους σταθμούς;' : 'Can I add more stations?'}
            </h4>
            <p className="text-white/70 text-sm">
              {currentLang === 'el' 
                ? 'Ναι! Μπορείτε να προσθέσετε επιπλέον σταθμούς. Επικοινωνήστε μαζί μας μέσω της φόρμας επικοινωνίας για να ενεργοποιήσουμε την πρόσβαση.'
                : 'Yes! You can add additional stations. Contact us through the contact form to activate access.'}
            </p>
          </div>
        </div>
        
        {/* Feature Explanations - 2 per row, in menu order */}
        <h4 className="text-lg font-semibold text-white mb-4 text-center">
          {currentLang === 'el' ? 'Τι περιλαμβάνει κάθε λειτουργία;' : 'What does each feature include?'}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          
          {/* 1. On Air Content */}
          <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-5 border border-purple-500/30">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="text-[#FF9F0A]">📡</span>
              {currentLang === 'el' ? 'Τι είναι το On Air Content;' : 'What is On Air Content?'}
            </h4>
            <p className="text-white/70 text-sm">
              {currentLang === 'el' 
                ? 'Έτοιμο, έξυπνο και προσαρμοσμένο περιεχόμενο για την on air εκπομπή των σταθμών. Βοηθά τους παραγωγούς να έχουν άμεση πρόσβαση σε χρήσιμες πληροφορίες για τη ροή του προγράμματος.'
                : 'Ready, smart and customized content for stations\' on-air broadcasts. Helps producers have instant access to useful information for program flow.'}
            </p>
          </div>
          
          {/* 2. The People's Chart - with PRO badge */}
          <div className="bg-gradient-to-r from-teal-900/30 to-cyan-900/30 rounded-xl p-5 border border-teal-500/30">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="text-teal-400">📻</span>
              <span className="text-teal-300">The People's Chart</span>
              <span className="text-[10px] bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2 py-0.5 rounded-full font-bold">PRO</span>
            </h4>
            <p className="text-white/70 text-sm">
              {currentLang === 'el' 
                ? 'Τα καλύτερα τραγούδια που συγκεντρώνουν τις μεγαλύτερες βαθμολογίες από τους πίνακες καταγραφής με την καλύτερη listener behavior. Αντικατοπτρίζει την πραγματική ανταπόκριση του κοινού.'
                : 'The best songs that gather the highest scores from recording tables with the best listener behavior. Reflects real audience response.'}
            </p>
          </div>
          
          
          {/* 4. Radio Top Songs */}
          <div className="bg-gradient-to-r from-teal-900/30 to-cyan-900/30 rounded-xl p-5 border border-teal-500/30">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="text-teal-400">🏆</span>
              <span className="text-teal-300">Radio Top Songs</span>
              <span className="text-[10px] bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2 py-0.5 rounded-full font-bold">PRO</span>
            </h4>
            <p className="text-white/70 text-sm">
              {currentLang === 'el' 
                ? 'Τα τραγούδια που παίζονται περισσότερο ΜΟΝΟ από τους καλύτερους σταθμούς σε Ελλάδα και Κύπρο. Ανακαλύψτε τι προτιμούν οι κορυφαίοι σταθμοί της αγοράς.'
                : 'Songs played most ONLY by the best stations in Greece and Cyprus. Discover what the top stations in the market prefer.'}
            </p>
          </div>
          
          {/* 5. Campaign Calculator */}
          <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-5 border border-purple-500/30">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="text-[#FF9F0A]">📊</span>
              {currentLang === 'el' ? 'Τι είναι ο Campaign Calculator;' : 'What is Campaign Calculator?'}
            </h4>
            <p className="text-white/70 text-sm">
              {currentLang === 'el' 
                ? 'Υπολογίστε την απήχηση της διαφημιστικής σας καμπάνιας. Εισάγετε τον αριθμό spots και δείτε εκτιμήσεις για reach, frequency και GRPs βάσει πραγματικών δεδομένων ακροαματικότητας.'
                : 'Calculate your advertising campaign reach. Enter the number of spots and see estimates for reach, frequency and GRPs based on real audience data.'}
            </p>
          </div>
          
          {/* 6. Songs Analyzer */}
          <div className="bg-gradient-to-r from-teal-900/30 to-cyan-900/30 rounded-xl p-5 border border-teal-500/30">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
              <span className="text-teal-400">🎵</span>
              <span className="text-teal-300">Songs Analyzer</span>
              <span className="text-[10px] bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-2 py-0.5 rounded-full font-bold">PRO</span>
            </h4>
            <p className="text-white/70 text-sm">
              {currentLang === 'el' 
                ? 'Αναλύστε τα τραγούδια που παίζει ο σταθμός σας σε πραγματικό χρόνο. Παρακολουθήστε μεταδόσεις, μετρήστε retention και δείτε πλήρη στατιστικά για τη μουσική σας ροή.'
                : 'Analyze songs played on your station in real-time. Track broadcasts, measure retention and see complete statistics for your music flow.'}
            </p>
          </div>
          
        </div>
        
        {/* Station Selection FAQ */}
        <div className="mt-6 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl p-5 border border-purple-500/30">
          <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
            <span className="text-[#FF9F0A]">📻</span>
            {currentLang === 'el' ? 'Πώς επιλέγω τον σταθμό μου;' : 'How do I choose my station?'}
          </h4>
          <p className="text-white/70 text-sm">
            {currentLang === 'el' 
              ? 'Με το Ολοκληρωμένο Πακέτο έχετε πρόσβαση σε 1 σταθμό της επιλογής σας. Μετά την αγορά, επικοινωνήστε μαζί μας μέσω της φόρμας επικοινωνίας για να σας αναθέσουμε τον σταθμό σας.'
              : 'With the Complete Package, you have access to 1 station of your choice. After purchase, contact us through the contact form and we will assign your station.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
